import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { formatDate, formatMoney } from '../lib/format'
import {
  findDuplicates, looksLikeTenantCharge, type DuplicateCandidate,
} from '../lib/rules'
import type { ExpenseCategory, Payer, Unit } from '../lib/types'
import { Sheet } from '../components/Sheet'
import {
  MoneyField, DateField, SelectField, TextField, TextAreaField, CheckField,
} from '../components/Fields'
import { PhotoField } from '../components/PhotoField'

type CatKey =
  | 'cat_plomberie' | 'cat_vidange' | 'cat_eau' | 'cat_electricite'
  | 'cat_peinture' | 'cat_amende' | 'cat_menage' | 'cat_autre'

const CATEGORIES: ExpenseCategory[] = [
  'plomberie', 'vidange', 'eau', 'electricite',
  'peinture', 'amende', 'menage', 'autre',
]

const PAYERS: Payer[] = [
  'gerant', 'assistant', 'proprietaire', 'mere', 'locataire', 'autre',
]

/**
 * Saisie d'une depense. L'appareil photo passe en premier : on est
 * generalement devant la facture au moment de l'enregistrer.
 *
 * Deux controles automatiques, tous deux de simples signalements :
 *   - reparation a la charge du locataire selon l'Article 3
 *   - doublon possible avec une depense deja enregistree
 */
export function ExpenseSheet({
  period,
  units,
  onClose,
  onSaved,
}: {
  period: string
  units: Unit[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()

  const [spentOn, setSpentOn] = useState(new Date().toISOString().slice(0, 10))
  const [category, setCategory] = useState<ExpenseCategory>('plomberie')
  const [unitId, setUnitId] = useState('')
  const [supplier, setSupplier] = useState('')
  const [amount, setAmount] = useState<number | null>(null)
  const [paidBy, setPaidBy] = useState<Payer>('gerant')
  const [description, setDescription] = useState('')
  const [invoiceDoc, setInvoiceDoc] = useState<string | null>(null)
  const [tenantCharge, setTenantCharge] = useState(false)
  const [tenantChargeTouched, setTenantChargeTouched] = useState(false)

  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Article 3 : proposition, que l'on peut toujours contredire.
  const suggereLocataire = looksLikeTenantCharge(description, category)
  useEffect(() => {
    if (!tenantChargeTouched) setTenantCharge(suggereLocataire)
  }, [suggereLocataire, tenantChargeTouched])

  // Recherche de doublons des que montant, categorie et unite sont
  // renseignes. Volontairement cote client sur une fenetre de trois
  // mois : peu de lignes, et rien a envoyer au serveur a chaque frappe.
  useEffect(() => {
    if (amount === null || amount <= 0) {
      setDuplicates([])
      return
    }
    let actif = true
    void (async () => {
      const { data } = await supabase
        .from('expenses')
        .select('id, spent_on, amount, category, unit_id, supplier')
        .eq('category', category)
        .gte('spent_on', new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10))
      if (!actif) return
      const trouves = findDuplicates(
        { spent_on: spentOn, amount, category, unit_id: unitId || null },
        (data ?? []) as DuplicateCandidate[],
      )
      setDuplicates(trouves)
    })()
    return () => {
      actif = false
    }
  }, [amount, category, unitId, spentOn])

  // Une depense ne peut pas etre enregistree sans sa piece. Le
  // declencheur de db/008 le refuse aussi cote base : l'ecran evite
  // simplement d'envoyer une requete vouee a etre rejetee.
  const valide = amount !== null && amount > 0 && invoiceDoc !== null

  async function submit() {
    if (!valide) return
    setError(null)
    setSubmitting(true)

    const { error: e } = await supabase.from('expenses').insert({
      spent_on: spentOn,
      period,
      category,
      unit_id: unitId || null,
      supplier: supplier.trim() || null,
      amount,
      paid_by: paidBy,
      description: description.trim() || null,
      invoice_document: invoiceDoc,
      tenant_charge: tenantCharge,
      // Toute depense naît en attente : seul le proprietaire approuve,
      // et la base le refuserait autrement.
      status: 'en_attente',
      duplicate_of: duplicates[0]?.id ?? null,
    })

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={t('expense_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={!valide}
      error={error}
    >
      {/* La photo d'abord : c'est le geste attendu, et elle est
          obligatoire. */}
      <PhotoField
        label={t('expense_invoice')}
        required
        target={{ kind: 'facture', unitId: unitId || null, period }}
        documentId={invoiceDoc}
        onUploaded={setInvoiceDoc}
      />

      {!invoiceDoc && (
        <div className="banner banner--error">
          <strong>{t('expense_invoice_required')}</strong>
          <span>{t('expense_invoice_required_body')}</span>
        </div>
      )}

      <MoneyField
        label={t('expense_amount')}
        required
        value={amount}
        onChange={setAmount}
      />

      <SelectField<ExpenseCategory>
        label={t('expense_category')}
        required
        value={category}
        onChange={setCategory}
        options={CATEGORIES.map((c) => ({
          value: c,
          label: t(`cat_${c}` as CatKey),
        }))}
      />

      <SelectField
        label={t('expense_unit')}
        value={unitId}
        onChange={setUnitId}
        options={[
          { value: '', label: t('expense_whole_building') },
          ...units.map((u) => ({ value: u.id, label: u.name })),
        ]}
      />

      <DateField
        label={t('expense_date')}
        required
        value={spentOn}
        onChange={setSpentOn}
      />

      <SelectField<Payer>
        label={t('expense_paid_by')}
        required
        value={paidBy}
        onChange={setPaidBy}
        options={PAYERS.map((p) => ({
          value: p,
          label: t(`payer_${p}` as
            'payer_gerant' | 'payer_assistant' | 'payer_proprietaire'
            | 'payer_mere' | 'payer_locataire' | 'payer_autre'),
        }))}
      />

      <TextField
        label={t('expense_supplier')}
        hint={t('optional')}
        value={supplier}
        onChange={setSupplier}
      />

      <TextAreaField
        label={t('expense_description')}
        hint={t('optional')}
        value={description}
        onChange={setDescription}
      />

      {/* Article 3 : signale, pas impose. */}
      {suggereLocataire && (
        <div className="banner banner--warn">
          <strong>{t('tenant_charge_flag')}</strong>
          <span>{t('tenant_charge_detected')}</span>
        </div>
      )}

      <CheckField
        label={t('tenant_charge_flag')}
        value={tenantCharge}
        onChange={(v) => {
          setTenantChargeTouched(true)
          setTenantCharge(v)
        }}
      />

      {/* Doublon possible : le controle qui existe parce que des
          travaux ont deja ete payes deux fois. */}
      {duplicates.length > 0 && (
        <div className="banner banner--error">
          <strong>{t('duplicate_warning_title')}</strong>
          <span>{t('duplicate_warning_body')}</span>
          {duplicates.map((d) => (
            <span key={d.id}>
              {formatDate(d.spent_on)} · {formatMoney(d.amount)}
              {d.supplier ? ` · ${d.supplier}` : ''}
            </span>
          ))}
        </div>
      )}
    </Sheet>
  )
}
